import { Injectable } from '@angular/core';

import JSZip from 'jszip';
import { catchError, firstValueFrom, of } from 'rxjs';

import { VersionResponse } from '@app/api/marketplace/models';
import { VersionService } from '@app/api/marketplace/services';
import { environment } from '@environments/environment';

import {
  BUNDLE_POLICY_VERSION,
  DIAGNOSTIC_REDACTION_PATTERNS,
} from '../constants/diagnostic-redaction.const';
import { DiagnosticEvent } from '../models/diagnostic-event';
import { DiagnosticProfile } from '../models/diagnostic-profile';
import { BundleSanitizerService } from './bundle-sanitizer.service';
import { CorrelationService } from './correlation.service';
import { DiagnosticBufferService } from './diagnostic-buffer.service';
import { TraceContextService } from './trace-context.service';

export interface AssembleInput {
  profile: DiagnosticProfile;
  problem: string;
  expected: string;
  screenshot?: Blob | null;
}

export interface BundleManifest {
  bundleVersion: number;
  policyVersion: number;
  profile: string;
  environment: string;
  appArtifact: string;
  appVersion: string;
  sessionPrefix: string;
  timeWindow: {
    maxMs: number;
    fromTimestamp: number | null;
    toTimestamp: number | null;
  };
  capturedAt: string;
  userDescription: {
    problem: string;
    expected: string;
  };
}

const BUNDLE_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class BundleAssemblyService {
  constructor(
    private readonly buffer: DiagnosticBufferService,
    private readonly traceContext: TraceContextService,
    private readonly versionService: VersionService,
    private readonly sanitizer: BundleSanitizerService,
    private readonly correlation: CorrelationService
  ) {}

  async assembleAndDownload(input: AssembleInput): Promise<void> {
    const capturedAt = new Date();
    // Sanitize before anything reaches the ZIP. Apply the same policy to
    // the user's free-text description in case they pasted a token.
    const sanitizedEvents = this.sanitizer.sanitizeEvents(
      this.buffer.snapshot()
    );
    const sanitizedInput: AssembleInput = {
      profile: input.profile,
      problem: this.sanitizer.sanitizeString(input.problem),
      expected: this.sanitizer.sanitizeString(input.expected),
    };
    const [version, rawCorrelation] = await Promise.all([
      this.fetchVersion(),
      this.correlation.assemble(),
    ]);
    const correlation = this.sanitizer.sanitizeCorrelation(rawCorrelation);
    const manifest = this.buildManifest({
      input: sanitizedInput,
      events: sanitizedEvents,
      version,
      capturedAt,
    });
    const readme = this.buildReadme(manifest);
    const zip = new JSZip();
    zip.file('manifest.json', JSON.stringify(manifest, null, 2));
    zip.file('events.json', JSON.stringify(sanitizedEvents, null, 2));
    zip.file('correlation.json', JSON.stringify(correlation, null, 2));
    if (input.screenshot) {
      zip.file('screenshot.png', input.screenshot);
    }
    zip.file('readme.txt', readme);
    const blob = await zip.generateAsync({ type: 'blob' });

    const filename = this.buildFilename(manifest.sessionPrefix, capturedAt);
    this.triggerDownload(blob, filename);
    this.navigateToMailto(
      this.buildMailto(manifest.sessionPrefix, manifest.environment)
    );
  }

  buildManifest(args: {
    input: AssembleInput;
    events: readonly DiagnosticEvent[];
    version: VersionResponse;
    capturedAt: Date;
  }): BundleManifest {
    const { input, events, version, capturedAt } = args;
    const fromTimestamp = events.length > 0 ? events[0].timestamp : null;
    const toTimestamp =
      events.length > 0 ? events[events.length - 1].timestamp : null;

    return {
      bundleVersion: BUNDLE_VERSION,
      policyVersion: BUNDLE_POLICY_VERSION,
      profile: input.profile.name,
      environment: environment.environmentName,
      appArtifact: version.artifact ?? 'unknown',
      appVersion: version.version ?? 'unknown',
      sessionPrefix: this.traceContext.getSessionPrefix(),
      timeWindow: {
        maxMs: input.profile.maxWindowMs,
        fromTimestamp,
        toTimestamp,
      },
      capturedAt: capturedAt.toISOString(),
      userDescription: {
        problem: input.problem,
        expected: input.expected,
      },
    };
  }

  buildReadme(manifest: BundleManifest): string {
    return [
      'EasyLot diagnosztikai bundle',
      '============================',
      '',
      `Készült: ${manifest.capturedAt}`,
      `Session prefix: ${manifest.sessionPrefix}`,
      `Profil: ${manifest.profile}`,
      `Környezet: ${manifest.environment}`,
      `App: ${manifest.appArtifact} ${manifest.appVersion}`,
      '',
      'Tartalom:',
      '- manifest.json     — bundle metadata',
      '- events.json       — kliensoldali események (route/API/error/user_action)',
      '- correlation.json  — operatív Loki/Grafana keresési segédadatok',
      '- screenshot.png    — pillanatkép a trigger pillanatában (ha sikerült)',
      '- readme.txt        — ez a fájl',
      '',
      'Felhasználói leírás:',
      '',
      'Mi volt a probléma?',
      '-------------------',
      manifest.userDescription.problem,
      '',
      'Mi lett volna az elvárt működés?',
      '--------------------------------',
      manifest.userDescription.expected,
      '',
      `Adatmaszkolás (policy v${manifest.policyVersion}):`,
      '- JWT-k a bundle-ben dekódolt claim-szintű formára (iss/sub/exp/hash) maszkolva.',
      `- URL query paraméterek értéke ***-re cserélve: ${DIAGNOSTIC_REDACTION_PATTERNS.forbiddenQueryParams.join(
        ', '
      )}.`,
      `- Headerek (ha valaha event-ben szerepelnek) kivágva: ${DIAGNOSTIC_REDACTION_PATTERNS.forbiddenHeaders.join(
        ', '
      )}.`,
      '- A maszkolás best-effort — érdemes átnézni a bundle tartalmát mielőtt elküldöd;',
      '  a fenti lista bővülhet a jövőben.',
      '',
      'Privacy és felhasználás:',
      '- Ez a csomag a felhasználó által észlelt hiba kivizsgálását szolgálja, és csak az',
      '  EasyLot support csapata által, belső support kontextusban használható. A merchantId',
      '  és a képernyőkép a felhasználó merchant kontextusát tükrözi — bizalmasan kezelendő.',
      '- A screenshot a renderelt DOM-ot ábrázolja, de a Material overlay-ek (dialog,',
      '  snackbar) és cross-origin képek nem mindig renderelődnek pontosan az html2canvas',
      '  korlátai miatt.',
      '',
    ].join('\n');
  }

  buildFilename(sessionPrefix: string, capturedAt: Date): string {
    const compact = capturedAt
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, 'Z');
    return `easylot-diagnostic-${sessionPrefix}-${compact}.zip`;
  }

  buildMailto(sessionPrefix: string, env: string): string {
    // RFC 6068: mailto URIs use percent-encoding (RFC 3986), not the
    // x-www-form-urlencoded "+" for space that URLSearchParams produces.
    // Outlook desktop renders the "+" literally otherwise.
    const subject = encodeURIComponent(
      `[EasyLot diagnostic ${sessionPrefix}] ${env}`
    );
    const body = encodeURIComponent(
      'Kérlek csatold a letöltött ZIP fájlt ehhez a levélhez, és írj le minden további körülményt amit hasznosnak tartasz.'
    );
    return `mailto:${environment.supportEmail}?subject=${subject}&body=${body}`;
  }

  private async fetchVersion(): Promise<VersionResponse> {
    return firstValueFrom(
      this.versionService
        .getVersion()
        .pipe(catchError(() => of({} as VersionResponse)))
    );
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  protected navigateToMailto(url: string): void {
    // Use a transient <a target="_blank"> click rather than
    // `window.location.href = mailto:...`. The latter triggers Chrome's
    // mailto protocol-handler prompt and can race the download, and on
    // hosts with no configured mail handler it silently navigates away.
    // An anchor with target="_blank" stays on the current page and lets
    // the browser hand off the mailto: URI to the OS handler if present.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}
